import React, { useState } from "react";
import axios from "axios";
import { X, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Money formatter that handles all supported currencies. */
const _fmt = (amount, currency) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency", currency, maximumFractionDigits: 2,
  }).format(amount);

/**
 * Partner bundle checkout modal.
 *
 * Anonymous (no login required) — the partner enters name / company /
 * email, clicks Pay, and we send them to Stripe. On payment success we
 * auto-provision their account server-side and email them a set-password
 * link. This lowers the friction from "apply → wait for review → wait for
 * payment link → pay → get account" down to a single click.
 *
 * Props:
 *  - bundle: the bundle object from /api/partners/bundles (must include
 *    id, name, price, currency, qty, tier, discount_pct)
 *  - onClose: () => void
 */
export default function PartnerCheckoutModal({ bundle, onClose }) {
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  if (!bundle) return null;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    // Currency-based gateway routing. Paystack accepts NGN/GHS/ZAR/KES;
    // Stripe accepts everything else. Same success page handles both.
    const currency = (bundle.currency || "USD").toUpperCase();
    const usePaystack = ["NGN", "GHS", "ZAR", "KES"].includes(currency);
    try {
      if (usePaystack) {
        const res = await axios.post(`${API}/paystack/checkout`, {
          purchase_type: "partner_bundle",
          bundle_id: bundle.id,
          partner_name: name.trim(),
          partner_company: company.trim(),
          email: email.trim().toLowerCase(),
          currency,
          origin_url: window.location.origin,
        });
        window.location.href = res.data.authorization_url;
      } else {
        const res = await axios.post(`${API}/partners/checkout`, {
          bundle_id: bundle.id,
          name: name.trim(),
          company: company.trim(),
          email: email.trim().toLowerCase(),
          currency,
          origin_url: window.location.origin,
        });
        window.location.href = res.data.url;
      }
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Could not start checkout. Please try again.");
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-navy-900/60 backdrop-blur-sm flex items-center justify-center px-4"
      onClick={onClose}
      data-testid="partner-checkout-modal"
    >
      <div
        className="bg-cream-100 rounded-3xl max-w-md w-full shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-6 border-b border-navy-700/10">
          <div>
            <p className="tracking-widest text-coral-400 text-xs font-bold uppercase">
              Partner checkout
            </p>
            <h2 className="font-serif text-navy-700 text-2xl mt-1">{bundle.name}</h2>
            <p className="text-navy-700/60 text-sm mt-1">
              {bundle.qty} × {bundle.tier} tier event credits · {bundle.discount_pct}% off retail
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            data-testid="partner-checkout-close"
            className="text-navy-700/50 hover:text-navy-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={submit} className="p-6 space-y-4">
          <div>
            <label className="text-xs font-semibold text-navy-700/70 uppercase tracking-wider">Your name</label>
            <input
              value={name} onChange={(e) => setName(e.target.value)}
              required maxLength={120} autoFocus
              data-testid="partner-checkout-name"
              className="w-full mt-1 border border-navy-700/15 rounded-xl px-4 py-3 bg-white focus:outline-none focus:border-coral-300"
              placeholder="Sarah Bella"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-navy-700/70 uppercase tracking-wider">Company</label>
            <input
              value={company} onChange={(e) => setCompany(e.target.value)}
              required maxLength={200}
              data-testid="partner-checkout-company"
              className="w-full mt-1 border border-navy-700/15 rounded-xl px-4 py-3 bg-white focus:outline-none focus:border-coral-300"
              placeholder="Roses & Rye Weddings"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-navy-700/70 uppercase tracking-wider">Email</label>
            <input
              value={email} onChange={(e) => setEmail(e.target.value)}
              required type="email" maxLength={200}
              data-testid="partner-checkout-email"
              className="w-full mt-1 border border-navy-700/15 rounded-xl px-4 py-3 bg-white focus:outline-none focus:border-coral-300"
              placeholder="sarah@rosesnrye.co"
            />
            <p className="text-[11px] text-navy-700/50 mt-1">
              We&apos;ll email your login link here after payment.
            </p>
          </div>

          <div className="border-t border-navy-700/10 pt-4">
            <div className="flex items-baseline justify-between">
              <span className="text-navy-700/70 text-sm">Total due today</span>
              <span className="text-navy-700 font-bold text-2xl">
                {_fmt(bundle.price, bundle.currency || "USD")}
              </span>
            </div>
            <p className="text-[11px] text-navy-700/50 mt-1">
              Secure checkout via Stripe. Cards, Apple Pay &amp; Google Pay accepted.
            </p>
          </div>

          {err && (
            <div
              data-testid="partner-checkout-error"
              className="text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm"
            >
              {err}
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            data-testid="partner-checkout-submit"
            className="w-full bg-coral-300 hover:bg-coral-400 text-white font-semibold py-3.5 rounded-full transition-colors disabled:opacity-70 disabled:cursor-wait inline-flex items-center justify-center gap-2"
          >
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting to Stripe…</>
              : <>Pay {_fmt(bundle.price, bundle.currency || "USD")} &amp; activate</>}
          </button>
        </form>
      </div>
    </div>
  );
}
